import { Injectable } from "@angular/core";
import { Project } from "../model/form/project";
import { ProjectResponse } from "../model/response/project-response";
import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { ProjectRequest } from "../model/request/project-request";
import { catchError, Observable, of, tap, throwError } from "rxjs";
import { enviroment } from "src/environments/environment";
import { ProjectSummaryResponse } from "../model/response/ProjectSummaryResponse";
import { Image } from "../model/Image";
import { Share } from "../model/share";
import { Vcu } from "../model/form/vcu";
import { VcuRequest } from "../model/request/vcu-request";
import { Vehicle } from "../model/vehicle";
import { ProjectSharing } from "../model/project-sharing";
import { VcuResponse } from "../model/response/vcu-response";
import { Item } from "../model/Item";
import { SharingItem } from "../model/sharingItem";
import { Equipment } from "../model/equipment";
import { DEFAULT_IMAGE } from "../model/mockup/image-mockup";
import { IMAGE_SIZES } from "../model/image-sizes";
import { ImageUrlService } from "./image-url.service";
import { CreateProjectPostReq } from "../model/request/create-project-post-req";
import { UpdateProjectPutReq } from "../model/request/update-project-put-req";
import { ProjectMapping } from "../model/project-mapping";
import { MapProject } from "src/app/model/map-project";
import { ProjectLogs } from "../model/project-logs";
import { LogProject } from "src/app/model/log-project";
import { CreateProjectSharingPostResp } from "../model/response/create-project-sharing-post-resp";
import { VcuService } from "./vcu.service";
import { ProjectEquipments } from "../model/project-equipments";
import { ProjectsFindUserVcusGetResp } from "../model/response/find-user-vcus-get-resp";
import { Status } from "../model/enum/status";
import { GetAllPublicProjectsResponse } from "../public/public-project-response";
import { ShareArchivesRequest } from "../model/request/share-archive-request";
import { GetListSharedArchives } from "../model/response/list-shared-archives-response";

@Injectable({ providedIn: "root" })
export class ProjectService {
  httpOptions: {};
  projectRequest: ProjectRequest;
  projectUrl = enviroment.BASE_URL_API + "/user";
  idToken: string;
  static http: any;
  
  /**
   * Configuração de cache para projetos do dashboard
   * - CACHE_EXPIRATION_MS: Tempo de expiração do cache (5 minutos)
   * - Os projetos são armazenados no localStorage
   * - O cache é automaticamente limpo quando expira ou quando há modificações
   */
  private readonly CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutos
  private readonly CACHE_KEY_PROJECTS = 'dashboard_projects_cache';
  private readonly CACHE_KEY_SHARED = 'dashboard_shared_projects_cache';
  
  constructor(private http: HttpClient, private imageUrlService: ImageUrlService) {
    this.idToken = sessionStorage.getItem("token") || "" 
    this.httpOptions = {
            headers: new HttpHeaders({
              'Content-Type': 'application/json; charset=utf-8',
              'Authorization': 'Bearer ' + this.idToken
            })
          }
    this.projectRequest = new ProjectRequest();
  }

 

  // getProject(userID: number) : Observable<ProjectResponse> {
  //   return this.http.get<ProjectResponse>(this.projectUrl + '/'+ userID, this.httpOptions).pipe(
  //     tap(),
  //     catchError(this.handleError<ProjectResponse>(`getProject userID=${userID}`))
  //   );
  // }

  findAllProjectSummary(
    userID: string,
    statusProject?: string,
    statusVCU?: string,
    forceRefresh: boolean = false
  ): Observable<ProjectSummaryResponse[]> {
    // Verificar cache primeiro
    if (!forceRefresh) {
      const cachedData = this.getProjectsFromCache(userID, statusProject, statusVCU);
      if (cachedData) {
        return of(cachedData);
      }
    }

    let url = `${this.projectUrl}/${userID}/project`;
    if (statusProject && statusVCU) {
      url = `${url}?statusProject=${statusProject}&statusVcu=${statusVCU}`;
    } else if (statusProject) {
      url = `${url}?statusProject=${statusProject}`;
    } else if (statusVCU) {
      url = `${url}?statusVcu=${statusVCU}`;
    }
    return this.http
      .get<ProjectSummaryResponse[]>(url, this.httpOptions)
      .pipe(
        tap((data) => {
          // Salvar no cache após obter os dados
          this.saveProjectsToCache(userID, statusProject, statusVCU, data);
        }),
        catchError(
          this.handleError<ProjectSummaryResponse[]>(`findAllProjectSummary`)
        )
      );
  }

  /**
   * Projetos de um afiliado, lidos pelo tuner. Não passa pelo cache do dashboard (que é do
   * usuário logado, não do cliente) e não engole o erro: a API responde 403 com
   * `affiliate.no_active_plan` / `tuner.plan_required` e o painel precisa desse erro para
   * abrir o modal de gate — findAllProjectSummary devolve `undefined` no lugar do erro.
   */
  findClientProjectSummary(userID: string): Observable<ProjectSummaryResponse[]> {
    return this.http.get<ProjectSummaryResponse[]>(`${this.projectUrl}/${userID}/project`, this.httpOptions);
  }

  getProject(userID: string, projectID: string): Observable<ProjectResponse> {
    return this.http
      .get<ProjectResponse>(
        this.projectUrl + "/" + userID + "/project/" + projectID,
        this.httpOptions
      )
      .pipe(tap(), catchError(this.handleError<ProjectResponse>(`getProject`)));
  }

  getAllPublicProject(): Observable<ProjectResponse> {
    return this.http.get<ProjectResponse>(
      this.projectUrl + "/public/public_projects",
      this.httpOptions
    ).pipe(tap(), catchError(this.handleError<ProjectResponse>(`getAllPublicProject`))
    );
  }

  createProject(userID: string, project: CreateProjectPostReq) {
    return this.http
      .post<ProjectResponse>(
        this.projectUrl + "/" + userID + "/project",
        project,
        this.httpOptions
      )
      .pipe(
        tap(() => {
          // Limpar cache após criar projeto
          this.clearProjectsCache(userID);
        }),
        catchError(this.handleError<ProjectResponse>("addProject"))
      );
  }

  updateProject(
    userID: string,
    projectID: string,
    project: UpdateProjectPutReq
  ): Observable<ProjectResponse> {
    return this.http
      .put<ProjectResponse>(
        this.projectUrl + "/" + userID + "/project/" + projectID,
        project,
        this.httpOptions
      )
      .pipe(
        tap(() => {
          // Limpar cache após atualizar projeto
          this.clearProjectsCache(userID);
        })
      );
  }

  // deleteProject(id: number) : Observable<ProjectResponse> {
  //   return this.http.delete<ProjectResponse>(this.projectUrl + '/'+ id, this.httpOptions).pipe(
  //     tap(),
  //     catchError(this.handleError<ProjectResponse>('deleteProject'))
  //   );
  // }

  async getProjectDefault(userID: string): Promise<Project | null> {
    return new Promise((resolve) => {
      this.findAllProjectSummary(userID).subscribe((data) => {
        if (data) {
          let projects: Project[] = this.projectSummaryToProject(data);
          let projectsFavorite: Project[] = projects.filter(
            (pj) => pj.favorite
          );
          if (projectsFavorite.length > 0) resolve(projectsFavorite[0]);
        } else {
          resolve(null);
        }
      });
    });
  }

  projectSummaryToProject(projectSumResp: ProjectSummaryResponse[]): Project[] {
    if (projectSumResp.length < 1) return [];

    let projects: Project[] = [];
    projectSumResp.forEach((psr) => {
      let equipments: Equipment[] = [];
      psr.nickname.forEach((eq) => {
        let equipment = new Equipment(1, eq, 0, "");
        equipments.push(equipment);
      });

      const project = new Project(
        psr.projectID,
        psr.default,
        psr.projectName,
        null,
        psr.application,
        null,
        psr.tags,
        "",
        [],
        equipments,
        psr.primaryImage ? [new Image(0, psr.primaryImage)] : [],
        psr.attributes?.primaryImage
          ? new Image(0, psr.attributes?.primaryImage)
          : null,
        undefined,
        [],
        [],
        [],
        "",
        psr.attributes,
        "",
        psr.ownerUser ? psr.ownerUser : "",
        undefined,
        undefined,
        psr.likes
      );
      projects.push(project);
    });
    return projects;
  }

  toProjectRequest(project: Project, vehicle: Vehicle) {
    //TODO converter campos e montar a REQUEST do backend
    project.vehicle = vehicle;
    return new ProjectRequest(
      project.name,
      project.favorite,
      {
        "date-of-manufacture":
          project.vehicle?.yearDate?.toISOString().split("T")[0] ?? "",
        detail: project.detail,
        manufacturer: project.vehicle?.manufacturer ?? "",
        photo: this.imageToString(project.images),
        primaryImage: project.primaryImage?.name ?? "",
        "vehicle-model": project.vehicle?.model ?? "",
      },
      this.shareToProjectSharing(project.sharedWith),
      project.tags,
      this.vcuToVcuRequest(project.vcus)
    );
  }

  static toProject(projectResponse: ProjectResponse): Project {
    return new Project(
      projectResponse.projectID,
      projectResponse.default,
      projectResponse.projectName,
      projectResponse.status ? (projectResponse.status as Status) : null,
      projectResponse.applicationID && projectResponse.applicationName
      ? [new Item(projectResponse.applicationID, projectResponse.applicationName)]
      : [],
      new Vehicle(
        0,
        projectResponse.attributes?.manufacturer,
        projectResponse.attributes?.["vehicle-model"],
        projectResponse.attributes?.["date-of-manufacture"]
          ? new Date(projectResponse.attributes?.["date-of-manufacture"])
          : null
      ),
      
      projectResponse.tags,
      
      projectResponse.attributes.detail,
      this.vcuResponseToVcu(projectResponse.vcus),
      this.equipmentsResponseToEquipment(projectResponse.equipments),
      this.stringToImage(
        projectResponse.attributes?.photo,
        projectResponse.userID,
        projectResponse.projectID,
        "photo"
      ),
      this.stringToImage(
        projectResponse.attributes?.primaryImage,
        projectResponse.userID,
        projectResponse.projectID,
        "primaryImage"
      )[0],
      null,
      this.projectSharingToShare(projectResponse.sharings),
      this.ProjectMappingToMaps(projectResponse.maps),
      this.ProjectLogsToLog(projectResponse.logs),
      "",
      null,
      projectResponse.userID,
      "",
      projectResponse.isSharing,
      new Item(projectResponse.sharingProjectTypeID, projectResponse.sharingProjectTypeName),
      projectResponse.likes
    );
    
  }

  static toProjects(projectsResponse: ProjectResponse[]): Project[] {
    if (!projectsResponse) return [];

    let projects: Project[] = [];
    projectsResponse.map((projectResponse) =>
      projects.push(this.toProject(projectResponse))
    );
    return projects;
  }

  imageToString(images: Image[]): string {
    if (images && images.length > 0)
      return images.map((img) => img.name).join(",");
    return "";
  }

  static stringToImage(
    str: string | null,
    userID: string,
    projectID: string,
    typeFile: string,
  ): Image[] {
    if (!str || str === "") return [];
    const cdn = enviroment.BASE_URL_CDN_MEDIA;
    const { w: gw, h: gh } = IMAGE_SIZES.gallery;
    const { w: tw, h: th } = IMAGE_SIZES.galleryThumb;
    return str.split(",").map((name, index) => {
      const n = name.trim();
      const base = `${cdn}fit-in`;
      const path = `${userID}/${projectID}/${typeFile}/${n}`;
      return new Image(
        index, n,
        `${base}/${gw}x${gh}/${path}`,
        undefined, false,
        `${base}/${tw}x${th}/${path}`
      );
    });
  }

  setImageCard(project: Project, user: string) {
    if (
      project.primaryImage !== undefined &&
      project.primaryImage !== null &&
      project.primaryImage.name !== "image12"
    ) {
      return this.imageUrlService.projectStyle(user, project.id!, project.primaryImage.name, 'cardThumb');
    } else {
      return 'url("' + DEFAULT_IMAGE.href + '")';
    }
  }

  setImageCardFavoriteProjects(project: GetAllPublicProjectsResponse, user: string) {
    if (
      project.attributes !== undefined &&
      project.attributes.primaryImage !== undefined &&
      project.attributes.primaryImage !== null
    ) {
      return this.imageUrlService.projectStyle(user, project.projectID, project.attributes.primaryImage, 'cardThumb');
    } else {
      return 'url("' + DEFAULT_IMAGE.href + '")';
    }
  }

  shareToProjectSharing(share: Share[]): ProjectSharing[] {
    if (!share) return [];

    // let shares: ProjectSharing[] = []
    // share.map(sh => shares.push(
    //   {
    //     UserID: sh.id,
    //     Permission: sh.permission
    //   }
    // ));
    // return shares;
    return [];
  }

  static ProjectMappingToMaps(maps: ProjectMapping[]): MapProject[] {
    if (!maps) return [];

    let mapsProject: MapProject[] = [];
    maps.map((map) =>
      mapsProject.push(
        new MapProject(
          map.id,
          map.fileName,
          map.projectName,
          [],
          map.createdDate
        )
      )
    );
    return mapsProject;
  }

  vcuToVcuRequest(vcus: Vcu[]): VcuRequest[] {
    if (!vcus) return [];

    let vcusReq: VcuRequest[] = [];
    vcus.map((vcu) => vcusReq.push(VcuService.toVcuRequest(vcu)));
    return vcusReq;
  }

  static vcuResponseToVcu(vcusResp: VcuResponse[]): Vcu[] {
    if (!vcusResp) return [];

    let vcus: Vcu[] = [];
    vcusResp.map((vcu) => vcus.push(VcuService.toVcu(vcu)));
    return vcus;
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      console.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  disableProject(userID: string, projectID: string) {
    return this.http
      .delete(
        this.projectUrl + "/" + userID + "/project/" + projectID,
        this.httpOptions
      )
      .pipe(
        tap(() => {
          // Limpar cache após desabilitar projeto
          this.clearProjectsCache(userID);
        }),
        catchError(this.handleError("disableProject"))
      );
  }

  static projectSharingToShare(share: ProjectSharing[]): Share[] {
    if (!share) return [];

    let shares: Share[] = [];
    share.map((sh) =>
      shares.push(
        new Share(sh.userId, sh.name, sh.email, sh.createdDate, sh.permission)
      )
    );
    return shares;
  }

  static ProjectLogsToLog(logs: ProjectLogs[]): LogProject[] {
    if (!logs) return [];

    let logProjects: LogProject[] = [];
    logs.map((sh) =>
      logProjects.push(
        //TODO BACKEND pedir para vir o Email e a data do Share
        new LogProject(sh.id, sh.fileName, sh.projectName, [], sh.createdDate)
      )
    );
    return logProjects;
  }

  CreateProjectSharing(
    projectID: string,
    userID: string,
    email: string
  ): Observable<CreateProjectSharingPostResp> {
    return this.http
      .post<CreateProjectSharingPostResp>(
        this.projectUrl + "/" + userID + "/project/" + projectID + "/sharing",
        { email: email },
        this.httpOptions
      )
      .pipe(
        tap(() => {
          // Limpar cache após criar compartilhamento
          this.clearProjectsCache(userID);
        }),
        catchError(
          this.handleError<CreateProjectSharingPostResp>(`FindUserEmail`)
        )
      );
  }

  DeleteProjectMap(userID: string, projectID: string, mapID: string) {
    let url = `${this.projectUrl}/${userID}/project/${projectID}/map/${mapID}`;
    return this.http.delete<void>(url, this.httpOptions).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  DeleteProjectLog(userID: string, projectID: string, logID: string) {
    let url = `${this.projectUrl}/${userID}/project/${projectID}/log/${logID}`;
    return this.http.delete<void>(url, this.httpOptions).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  DeleteProjectShare(userID: string, projectID: string, shareID: string) {
    let url = `${this.projectUrl}/${userID}/project/${projectID}/share/${shareID}`;
    return this.http.delete(url, this.httpOptions).pipe(
      tap(() => {
        // Limpar cache após deletar compartilhamento
        this.clearProjectsCache(userID);
      }),
      catchError(this.handleError("DeleteProjectShare"))
    );
  }

  static equipmentsResponseToEquipment(
    projectEquipments: ProjectEquipments[]
  ): Equipment[] {
    if (!projectEquipments) return [];

    let equipments: Equipment[] = [];
    projectEquipments.map((projectEquipment) =>
      equipments.push(
        new Equipment(
          projectEquipment.equipmentID,
          projectEquipment.equipmentName,
          projectEquipment.categoryID,
          projectEquipment.categoryName
        )
      )
    );
    return equipments;
  }

  static userProjectToProject(projectResp: ProjectsFindUserVcusGetResp): Project {
    
return new Project(
    projectResp.projectID ?? "",
    projectResp.default ?? false,
    projectResp.projectName ?? "",
    projectResp.status ? (projectResp.status as Status) : null,
    projectResp.applicationID
      ? [new Item(projectResp.applicationID)]
      : [],
    null,
    projectResp.tags ?? [],
    "",
    [],
    [],
    projectResp.primaryImage ? [new Image(0, projectResp.primaryImage)] : [],
    projectResp.attributes?.primaryImage
      ? new Image(0, projectResp.attributes?.primaryImage)
      : null,
    null,
    projectResp.sharings
      ? this.projectSharingToShare(projectResp.sharings)
      : [],
    projectResp.maps ? this.ProjectMappingToMaps(projectResp.maps) : [],
    projectResp.logs ? this.ProjectLogsToLog(projectResp.logs) : [],
    "",
    projectResp.attributes ?? null,
    projectResp.sharingProjectTypeID !== null &&
    projectResp.sharingProjectTypeID !== undefined
      ? projectResp.sharingProjectTypeID.toString()
      : undefined
  );

    
  }
  

  static userProjectToProjects(
    projectsResp: ProjectsFindUserVcusGetResp[]
  ): Project[] {
    let projects: Project[] = [];
    if (projectsResp) {
      for (let proj of projectsResp) {
        projects.push(ProjectService.userProjectToProject(proj));
      }
    }
    return projects;
  }

  FindProjectsSharedSummary(
    userID: string,
    forceRefresh: boolean = false
  ): Observable<ProjectSummaryResponse[]> {
    // Verificar cache primeiro
    if (!forceRefresh) {
      const cachedData = this.getSharedProjectsFromCache(userID);
      if (cachedData) {
        return of(cachedData);
      }
    }

    let url = `${this.projectUrl}/${userID}/project/shared`;
    return this.http.get<ProjectSummaryResponse[]>(url, this.httpOptions).pipe(
      tap((data) => {
        // Salvar no cache após obter os dados
        this.saveSharedProjectsToCache(userID, data);
      }),
      catchError(this.handleError<ProjectSummaryResponse[]>(`FindProjectsSharedSummary`))
    );
  }

  ShareArchives(userID:string, projectID: string, fileID: string, shareInformations: ShareArchivesRequest): Observable<HttpResponse<any>> {
    return this.http.post<any>(this.projectUrl + `/${userID}/project/${projectID}/shared/file/${fileID}`, shareInformations,{...this.httpOptions, observe: 'response' }).pipe(
      tap(),
      catchError(this.handleError<any>(`ShareArchives`))
    );
  }
  GetSharedArchives(userID: string, sharedWithMe: boolean, page: number, pageItens: number): Observable<HttpResponse<any>> {
    let url = `${this.projectUrl}/${userID}/project/shared/files?sharedWithMe=${sharedWithMe}&page=${page}&pageItens=${pageItens}`;
    return this.http.get<GetListSharedArchives>(url, {...this.httpOptions, observe: 'response' }).pipe(
      tap(),
      catchError(this.handleError<any>(`GetSharedArchives`))
    );
  }
  RemoveSharedArchive(userID: string, sharedID: string, accept: boolean): Observable<HttpResponse<any>> {
    let url = `${this.projectUrl}/${userID}/project/shared/file/${sharedID}`;
    const param = { accept: accept }
    return this.http.post<any>(url, param, {...this.httpOptions, observe: 'response', responseType: 'text' as 'json'}).pipe()
  }

  // Métodos de gerenciamento de cache
  private getCacheKey(userID: string, statusProject?: string, statusVCU?: string): string {
    return `${this.CACHE_KEY_PROJECTS}_${userID}_${statusProject || 'all'}_${statusVCU || 'all'}`;
  }

  private getSharedCacheKey(userID: string): string {
    return `${this.CACHE_KEY_SHARED}_${userID}`;
  }

  private saveProjectsToCache(
    userID: string,
    statusProject: string | undefined,
    statusVCU: string | undefined,
    data: ProjectSummaryResponse[]
  ): void {
    const cacheKey = this.getCacheKey(userID, statusProject, statusVCU);
    const cacheData = {
      data: data,
      timestamp: Date.now()
    };
    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Erro ao salvar cache:', error);
    }
  }

  private getProjectsFromCache(
    userID: string,
    statusProject?: string,
    statusVCU?: string
  ): ProjectSummaryResponse[] | null {
    const cacheKey = this.getCacheKey(userID, statusProject, statusVCU);
    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      const now = Date.now();

      // Verificar se o cache expirou
      if (now - cacheData.timestamp > this.CACHE_EXPIRATION_MS) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return cacheData.data;
    } catch (error) {
      console.warn('Erro ao ler cache:', error);
      return null;
    }
  }

  private saveSharedProjectsToCache(userID: string, data: ProjectSummaryResponse[]): void {
    const cacheKey = this.getSharedCacheKey(userID);
    const cacheData = {
      data: data,
      timestamp: Date.now()
    };
    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Erro ao salvar cache de projetos compartilhados:', error);
    }
  }

  private getSharedProjectsFromCache(userID: string): ProjectSummaryResponse[] | null {
    const cacheKey = this.getSharedCacheKey(userID);
    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      const now = Date.now();

      // Verificar se o cache expirou
      if (now - cacheData.timestamp > this.CACHE_EXPIRATION_MS) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return cacheData.data;
    } catch (error) {
      console.warn('Erro ao ler cache de projetos compartilhados:', error);
      return null;
    }
  }

  /**
   * Limpa o cache de projetos do usuário
   * @param userID ID do usuário (opcional). Se não fornecido, limpa todo o cache de projetos
   */
  clearProjectsCache(userID?: string): void {
    try {
      if (userID) {
        // Limpar cache específico do usuário
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith(`${this.CACHE_KEY_PROJECTS}_${userID}`) || 
              key.startsWith(`${this.CACHE_KEY_SHARED}_${userID}`)) {
            localStorage.removeItem(key);
          }
        });
      } else {
        // Limpar todo o cache de projetos
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith(this.CACHE_KEY_PROJECTS) || 
              key.startsWith(this.CACHE_KEY_SHARED)) {
            localStorage.removeItem(key);
          }
        });
      }
    } catch (error) {
      console.warn('Erro ao limpar cache:', error);
    }
  }

  confirmParticipation(userID: string, projectID: string): Observable<any> {
    return this.http.post<any>(this.projectUrl + `/${userID}/project/${projectID}/confirm_vp`, this.httpOptions).pipe(
      tap(),
      catchError(this.handleError<any>(`confirmParticipation`))
    );
  }

}
